import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { GridTileImage } from '../components/grid/tile';
import Footer from '../components/layout/footer';
import { createClient } from '@supabase/supabase-js';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { ShoppingBagIcon, StarIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Product } from '../lib/burokshop/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface RestaurantProfile {
  id: string;
  business_name: string;
  business_description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  business_phone: string | null;
  business_email: string | null;
  street: string;
  city: string;
  rating: number;
  review_count: number;
  total_orders: number;
  is_verified: boolean;
  badge_verified: boolean;
  is_open: boolean;
  opening_hours: any;
}

const MarketplaceSection = ({
  products
}: {
  products: Product[];
}) => {
  if (products.length === 0) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white/50 p-8 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
        <p className="text-lg text-neutral-500 dark:text-neutral-400">
          Aucun produit disponible pour le moment
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4">
      {products.map((product) => (
        <Link
          key={product.id}
          to={`/product/${product.handle}`}
          className="group"
        >
          <GridTileImage
            alt={product.title}
            label={{
              title: product.title,
              amount: product.priceRange.maxVariantPrice.amount,
              currencyCode: product.priceRange.maxVariantPrice.currencyCode
            }}
            src={product.featuredImage?.url}
            width={200}
            height={200}
            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 25vw, 33vw"
          />
        </Link>
      ))}
    </div>
  );
};

export default function RestaurantProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<RestaurantProfile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowingAnimating, setIsFollowingAnimating] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);

  // Fonction pour calculer le statut d'ouverture
  const getOpeningStatus = (openingHours: any) => {
    if (!openingHours || Object.keys(openingHours).length === 0) {
      return { isOpen: false, message: null };
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = dimanche, 1 = lundi, etc.
    const currentTime = now.getHours() * 100 + now.getMinutes(); // Format HHMM

    const dayMap: Record<number, string> = {
      0: 'dimanche',
      1: 'lundi',
      2: 'mardi',
      3: 'mercredi',
      4: 'jeudi',
      5: 'vendredi',
      6: 'samedi'
    };

    const dayOrder = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
    const currentDayName = dayMap[currentDay];
    const currentDayData = openingHours[currentDayName];

    // Vérifier si ouvert aujourd'hui
    if (currentDayData?.isOpen && currentDayData?.hours && currentDayData.hours.length > 0) {
      for (const hour of currentDayData.hours) {
        const [openH, openM] = hour.open.split(':').map(Number);
        const [closeH, closeM] = hour.close.split(':').map(Number);
        const openTime = openH * 100 + openM;
        const closeTime = closeH * 100 + closeM;

        if (currentTime >= openTime && currentTime < closeTime) {
          // Ouvert maintenant, retourner l'heure de fermeture
          return { isOpen: true, message: `Ouvert • Ferme à ${hour.close}` };
        }
      }
    }

    // Fermé maintenant, trouver la prochaine ouverture
    let daysToCheck = 7;
    let dayIndex = dayOrder.indexOf(currentDayName);
    
    for (let i = 0; i < daysToCheck; i++) {
      const checkDayIndex = (dayIndex + i) % 7;
      const checkDayName = dayOrder[checkDayIndex];
      const checkDayData = openingHours[checkDayName];

      if (checkDayData?.isOpen && checkDayData?.hours && checkDayData.hours.length > 0) {
        for (const hour of checkDayData.hours) {
          const [openH, openM] = hour.open.split(':').map(Number);
          const openTime = openH * 100 + openM;

          if (i === 0 && currentTime < openTime) {
            // Ouverture aujourd'hui mais pas encore
            return { isOpen: false, message: `Fermé • Ouvre à ${hour.open}` };
          } else if (i > 0) {
            // Ouverture un jour futur
            const dayNames: Record<string, string> = {
              lundi: 'Lundi',
              mardi: 'Mardi',
              mercredi: 'Mercredi',
              jeudi: 'Jeudi',
              vendredi: 'Vendredi',
              samedi: 'Samedi',
              dimanche: 'Dimanche'
            };
            return { isOpen: false, message: `${dayNames[checkDayName]} à ${hour.open}` };
          }
        }
      }
    }

    return { isOpen: false, message: null };
  };

  useEffect(() => {
    async function loadProfile() {
      if (!id) return;

      setLoading(true);
      try {
        // Récupérer le profil du restaurant
        const { data: profileData, error: profileError } = await supabase
          .from('restaurant_profiles')
          .select('*')
          .eq('id', id)
          .eq('is_active', true)
          .eq('is_live', true)
          .single();

        if (profileError) {
          console.error('Error loading profile:', profileError);
          setLoading(false);
          return;
        }

        if (profileData) {
          setProfile({
            id: profileData.id,
            business_name: profileData.business_name,
            business_description: profileData.business_description,
            logo_url: profileData.logo_url,
            banner_url: profileData.banner_url,
            business_phone: profileData.business_phone,
            business_email: profileData.business_email,
            street: profileData.street,
            city: profileData.city,
            rating: profileData.rating || 4.8,
            review_count: profileData.review_count || 0,
            total_orders: profileData.total_orders || 0,
            is_verified: profileData.is_verified || false,
            badge_verified: profileData.badge_verified || false,
            is_open: profileData.is_open || false,
            opening_hours: profileData.opening_hours || {}
          });

          // Récupérer les produits
          const { data: productsData, error: productsError } = await supabase
            .from('restaurant_products')
            .select('*')
            .eq('restaurant_id', profileData.id)
            .eq('is_active', true)
            .order('updated_at', { ascending: false })
            .limit(20);

          if (productsError) {
            console.error('Error loading products:', productsError);
          } else if (productsData) {
            // Mapper les produits depuis Supabase vers le format Product
            const mappedProducts: Product[] = productsData.map((row: any) => {
              const images = Array.isArray(row.images) ? row.images : [];
              const firstImage = images[0];
              const featuredImage = firstImage ? {
                url: typeof firstImage === 'string' ? firstImage : firstImage.url || firstImage,
                altText: row.name,
                width: 800,
                height: 800
              } : {
                url: '',
                altText: row.name,
                width: 800,
                height: 800
              };

              return {
                id: row.id,
                handle: row.name.toLowerCase().replace(/\s+/g, '-') + '-' + row.id,
                availableForSale: (row.stock_quantity ?? 0) > 0,
                title: row.name,
                description: row.description || '',
                descriptionHtml: row.description || '',
                options: [],
                priceRange: {
                  maxVariantPrice: {
                    amount: (row.original_price || row.price).toString(),
                    currencyCode: 'XOF'
                  },
                  minVariantPrice: {
                    amount: row.price.toString(),
                    currencyCode: 'XOF'
                  }
                },
                variants: [{
                  id: row.id,
                  title: 'Default',
                  availableForSale: (row.stock_quantity ?? 0) > 0,
                  selectedOptions: [],
                  price: {
                    amount: row.price.toString(),
                    currencyCode: 'XOF'
                  }
                }],
                featuredImage,
                images: images.map((img: any, index: number) => ({
                  url: typeof img === 'string' ? img : img.url || img,
                  altText: `${row.name} - ${index + 1}`,
                  width: 800,
                  height: 800
                })),
                seo: {
                  title: row.name,
                  description: row.description || row.name
                },
                tags: row.tags || [],
                updatedAt: row.updated_at || row.created_at
              };
            });

            setProducts(mappedProducts);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg text-neutral-500 dark:text-neutral-400">Un instant...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-neutral-900 dark:text-white">Restaurant non trouvé</h1>
          <p className="text-neutral-500 dark:text-neutral-400">
            Ce restaurant n'existe pas ou n'est pas disponible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
      {/* Banner */}
      {profile.banner_url && (
        <div className="relative h-48 w-full overflow-hidden sm:h-64 md:h-80">
          <img
            src={profile.banner_url}
            alt="Banner"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {/* Logo ShopLink au centre */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-white/20 backdrop-blur-sm blur-xl" />
              <img
                src="/assets/images/Shoplinkpng.png"
                alt="ShopLink Logo"
                className="relative h-16 w-auto object-contain drop-shadow-2xl sm:h-20 md:h-24"
              />
            </div>
          </div>
        </div>
      )}

      {/* Bannière défilante avec logos Burok */}
      <div className="relative w-full overflow-hidden bg-gradient-to-r from-[#ff0442] via-[#ff1a4d] to-[#ff0442] py-3">
        <div className="flex animate-scroll-left items-center gap-8 whitespace-nowrap">
          {[
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
          ].map((logo, index) => (
            <div key={`logo-1-${index}`} className="flex-shrink-0">
              <img
                src={logo}
                alt={`Burok Logo ${index + 1}`}
                className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
              />
            </div>
          ))}
          {[
            '/assets/BurokLogoModern/BurokModernLogoWhite.png',
            '/assets/BurokLogoModern/BurokModernLogoBlack.png',
            '/assets/BurokLogoModern/BurokModernLogoOrange.png',
            '/assets/BurokLogoModern/BurokModernLogoPurple.png',
            '/assets/BurokLogoModern/BurokModernLogoBlue.png',
            '/assets/BurokLogoModern/BurokModernLogoYellow.png',
            '/assets/BurokLogoModern/BurokModernLogoGreen.png',
            '/assets/BurokLogoModern/BurokModernLogoRed.png',
          ].map((logo, index) => (
            <div key={`logo-2-${index}`} className="flex-shrink-0">
              <img
                src={logo}
                alt={`Burok Logo ${index + 1}`}
                className="h-6 w-auto object-contain opacity-90 transition-opacity duration-300 hover:opacity-100 sm:h-8 md:h-10"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 pb-12 pt-8">
        {/* Profile Header */}
        <div className="mb-8 text-center">
          <div className="relative -mt-20 mb-4 inline-block">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 blur-xl opacity-50" />
            <img
              src={profile.logo_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200&auto=format&fit=crop'}
              alt={profile.business_name}
              className="relative h-32 w-32 rounded-full border-4 border-white object-cover shadow-2xl dark:border-neutral-800 sm:h-40 sm:w-40"
            />
          </div>
          <div className="mb-2 flex items-center justify-center gap-2">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white sm:text-4xl">
              {profile.business_name}
            </h1>
            {(profile.is_verified || profile.badge_verified) && (
              <img
                src="/assets/images/verified-check-svgrepo-com.svg"
                alt="Vérifié"
                className="mt-1 h-6 w-6 sm:mt-1.5 sm:h-7 sm:w-7"
              />
            )}
          </div>
          
          {/* Rating and Stats */}
          <div className="mb-4 flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-1">
              <StarIcon className="h-5 w-5 fill-yellow-400 text-yellow-400" />
              <span className="text-lg font-semibold text-neutral-900 dark:text-white">
                {profile.rating.toFixed(1)}
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                ({profile.review_count} avis)
              </span>
            </div>
            {(() => {
              const status = getOpeningStatus(profile.opening_hours);
              if (status.isOpen && status.message) {
                return (
                  <>
                    <div className="h-4 w-px bg-neutral-300 dark:bg-neutral-600" />
                    <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      {status.message}
                    </div>
                  </>
                );
              } else if (!status.isOpen && status.message) {
                return (
                  <>
                    <div className="h-4 w-px bg-neutral-300 dark:bg-neutral-600" />
                    <div className="flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      {status.message}
                    </div>
                  </>
                );
              } else if (profile.is_open) {
                return (
                  <>
                    <div className="h-4 w-px bg-neutral-300 dark:bg-neutral-600" />
                    <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      Ouvert
                    </div>
                  </>
                );
              }
              return null;
            })()}
            {profile.opening_hours && Object.keys(profile.opening_hours).length > 0 && (
              <>
                <div className="h-4 w-px bg-neutral-300 dark:bg-neutral-600" />
                <button
                  onClick={() => setShowHoursModal(true)}
                  className="flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-all hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                >
                  <ClockIcon className="h-4 w-4" />
                  Horaires
                </button>
              </>
            )}
          </div>

          {profile.business_description && (
            <p className="mx-auto max-w-2xl text-lg text-neutral-600 dark:text-neutral-300 mb-6">
              {profile.business_description}
            </p>
          )}

          {/* Contact Info */}
          <div className="mb-6 flex flex-wrap items-center justify-center gap-4">
            {profile.street && profile.city && (
              <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <MapPinIcon className="h-4 w-4" />
                <span>{profile.street}, {profile.city}</span>
              </div>
            )}
            {profile.business_phone && (
              <a
                href={`tel:${profile.business_phone}`}
                className="flex items-center gap-2 text-sm text-neutral-600 transition-colors hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400"
              >
                <PhoneIcon className="h-4 w-4" />
                <span>{profile.business_phone}</span>
              </a>
            )}
            {profile.business_email && (
              <a
                href={`mailto:${profile.business_email}`}
                className="flex items-center gap-2 text-sm text-neutral-600 transition-colors hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400"
              >
                <EnvelopeIcon className="h-4 w-4" />
                <span>{profile.business_email}</span>
              </a>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => {
                setIsFollowingAnimating(true);
                setTimeout(() => {
                  setIsFollowing(!isFollowing);
                  setIsFollowingAnimating(false);
                }, 300);
              }}
              className={`relative flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isFollowing
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                  : 'bg-white border-2 border-neutral-300 text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
              } ${isFollowingAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
            >
              {isFollowing ? (
                <>
                  <HeartIconSolid className="h-5 w-5 animate-pulse" />
                  <span>Suivi</span>
                </>
              ) : (
                <>
                  <HeartIcon className="h-5 w-5" />
                  <span>Suivre</span>
                </>
              )}
              {isFollowingAnimating && (
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
              )}
            </button>
            <Link
              to={`/restaurant/${profile.id}`}
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
            >
              <ShoppingBagIcon className="h-5 w-5" />
              Voir tous les produits
            </Link>
          </div>
        </div>

        {/* Products Section */}
        {products.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">
              Produits ({products.length})
            </h2>
            <MarketplaceSection products={products} />
          </div>
        )}
      </div>
      <Footer />

      {/* Modal des horaires */}
      {showHoursModal && profile.opening_hours && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowHoursModal(false)}>
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-neutral-800" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowHoursModal(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-700 dark:hover:text-white"
              aria-label="Fermer"
              title="Fermer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">Horaires d'ouverture</h2>
            <div className="space-y-3">
              {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day) => {
                const dayData = profile.opening_hours[day];
                const dayNames: Record<string, string> = {
                  lundi: 'Lundi',
                  mardi: 'Mardi',
                  mercredi: 'Mercredi',
                  jeudi: 'Jeudi',
                  vendredi: 'Vendredi',
                  samedi: 'Samedi',
                  dimanche: 'Dimanche'
                };
                return (
                  <div key={day} className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-700 dark:bg-neutral-900/50">
                    <span className="font-medium text-neutral-900 dark:text-white">{dayNames[day]}</span>
                    {dayData?.isOpen && dayData?.hours && dayData.hours.length > 0 ? (
                      <div className="flex flex-col items-end gap-1">
                        {dayData.hours.map((hour: any, index: number) => (
                          <span key={index} className="text-sm text-neutral-600 dark:text-neutral-400">
                            {hour.open} - {hour.close}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-red-600 dark:text-red-400">Fermé</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

